import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { matchPath, useLocation } from 'react-router';
import { EMPLOYER, OWNER, SUBOWNER } from '../../../../common/Authorization/entities';
import { EDIT, VIEW } from '../../../../common/Authorization/permissions';
import { HasAccessTo, IsSuperAdmin } from '../../../../components';
import { useDialog, useOwnerDeleteMutation, useOwnerGetDetailQuery, useSnakeBar } from '../../../../hooks';
import { useEmployersSearchQuery } from '../../../../hooks/api';
import { normalizeError } from 'app/utils';

const EMPLOYERS_DEFAULT_PAGE = 1;
const EMPLOYERS_DEFAULT_PAGE_SIZE = 25;
const EMPLOYERS_PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500, 1000];

export const useOwnerDetailPage = () => {
  const { pathname } = useLocation();
  const { mutate: deleteOwner } = useOwnerDeleteMutation();
  const { showErrorSnakeBar } = useSnakeBar();
  const navigate = useNavigate();
  const { ownerId } = useParams();

  const [employersPage, setEmployersPage] = React.useState(EMPLOYERS_DEFAULT_PAGE);
  const [employersPageSize, setEmployersPageSize] = React.useState(EMPLOYERS_DEFAULT_PAGE_SIZE);

  const isSuperAdmin = IsSuperAdmin();

  const {
    dialogState: showDeleteConfirmationDialog,
    closeDialog: closeDeleteConfirmationDialog,
    openDialog: openDeleteConfirmationDialog,
  } = useDialog();

  const {
    dialogState: showViewEmployersDialog,
    closeDialog: closeViewEmployersDialog,
    openDialog: openViewEmployersDialog,
  } = useDialog();

  const hasOwnerId = !!ownerId;
  const { data: owner, isLoading: isLoadingOwner } = useOwnerGetDetailQuery({
    variables: { ownerId: ownerId || '' },
    options: {
      enabled: hasOwnerId,
    },
  });

  const isOwner = owner?.parent === null;

  const { data: employersSearchData, isLoading: isLoadingOwnerEmployers } = useEmployersSearchQuery({
    variables: { page: employersPage, pageSize: employersPageSize },
    options: {
      enabled: showViewEmployersDialog && isOwner,
    },
  });

  const onEmployersPaginationChange = React.useCallback(({ page, rowSize }: { page?: number; rowSize?: number }) => {
    if (typeof rowSize !== 'undefined') {
      setEmployersPageSize(rowSize);
      setEmployersPage(EMPLOYERS_DEFAULT_PAGE);
    }
    if (typeof page !== 'undefined') {
      setEmployersPage(page);
    }
  }, []);

  const ownerEmployers = React.useMemo(() => {
    if (!employersSearchData?.items) return undefined;
    return [...employersSearchData.items].sort((a, b) => (a.employerName < b.employerName ? -1 : 0));
  }, [employersSearchData?.items]);

  React.useEffect(() => {
    if (owner) {
      const isOwnersPath = !!matchPath('/owners/*', pathname);
      const redirectPath = `/${isOwnersPath ? 'sub-owners' : 'owners'}/${ownerId}`;
      const shouldRedirect = (isOwnersPath && !isOwner) || (!isOwnersPath && isOwner);

      if (shouldRedirect) {
        navigate(redirectPath, { replace: true });
      }
    }
  }, [owner, pathname, navigate, ownerId, isOwner]);

  const disabledIfNoAccessToEdit = isOwner ? !HasAccessTo(OWNER, EDIT) : !HasAccessTo(SUBOWNER, EDIT);
  const disabledIfNoAccessToEmployerView = !HasAccessTo(EMPLOYER, VIEW);

  const disabledShowEmployersButtonIfLoadingOrNoAccess = isLoadingOwner || disabledIfNoAccessToEmployerView;
  const disabledDeleteButtonIfLoadingOrNoAccess = isLoadingOwner || disabledIfNoAccessToEdit;
  const existsOwner = isLoadingOwner || !!owner;

  const onClickConfirmDelete = React.useCallback(() => {
    if (ownerId) {
      deleteOwner(
        { ownerId },
        {
          onSuccess: () => navigate(isOwner ? '/owners' : '/owners/sub-owners'),
          onError: (error: any) => {
            const errors = normalizeError(error);
            showErrorSnakeBar({
              method: 'deleteOwner',
              message: errors.message,
            });
          },
        },
      );
    }
  }, [isOwner, deleteOwner, navigate, ownerId, showErrorSnakeBar]);

  return {
    ownerId,
    owner,
    isOwner,
    isSuperAdmin,
    ownerEmployers,
    isLoadingOwnerEmployers,
    employersPage,
    employersPageSize,
    employersPageSizeOptions: EMPLOYERS_PAGE_SIZE_OPTIONS,
    employersTotalCount: employersSearchData?.totalItems ?? 0,
    onEmployersPaginationChange,
    existsOwner,
    showDeleteConfirmationDialog,
    closeDeleteConfirmationDialog,
    openDeleteConfirmationDialog,
    showViewEmployersDialog,
    closeViewEmployersDialog,
    openViewEmployersDialog,
    disabledShowEmployersButtonIfLoadingOrNoAccess,
    disabledDeleteButtonIfLoadingOrNoAccess,
    onClickConfirmDelete,
  };
};
